import {
    ProtocolPatientStatusDetail,
    ProtocolPatientStatusStatistics
} from '../../../models/Entities/Protocols/ProtocolPatientStatusStatistics';
import * as PatientFunctions from './PatientStatusStatistics.Functions';
import { VisitCellState, VisitExecutionModel, formatMonth } from './VisitExecution.Functions';

/**
 * Exportación a Excel de las secciones de estadísticas.
 *
 * Las funciones que arman las hojas son puras —reciben datos, devuelven filas— y
 * se prueban solas. La descarga es lo único con efectos, y carga la librería recién
 * al usarla, como el visor de archivos: no hay por qué sumar la planilla a la carga
 * de cada pantalla.
 *
 * Formato de los archivos: cada hoja lleva un encabezado con el título del reporte,
 * el alcance y la fecha de generación; los datos van con autofiltro, anchos de
 * columna calculados y formato de número, moneda, porcentaje y fecha. Un libro con
 * varias hojas abre con una portada que dice qué trae cada una.
 *
 * Escribe con xlsx-js-style, que es el mismo SheetJS pero guarda los estilos de
 * celda: de ahí salen el título en banda, los encabezados en blanco sobre azul y los
 * cortes de control resaltados. El visor de archivos sigue leyendo con xlsx.
 */

//#region Celdas con formato

export type ExcelFormat = 'text' | 'integer' | 'money' | 'percent' | 'date';

/** Valor con formato de Excel: el número se guarda como número, y se ve como corresponde. */
export interface TypedCell {
    value: string | number | Date | null | undefined;
    format: ExcelFormat;
    /** Resalta la celda en negrita, además del estilo de su fila. */
    bold?: boolean;
}

export type ExcelCell = string | number | boolean | Date | null | undefined | TypedCell;
export type ExcelRow = Record<string, ExcelCell>;

export interface ExcelSheet {
    name: string;
    rows: ExcelRow[];
    /** Título del bloque de encabezado. Por defecto, el nombre de la hoja. */
    title?: string;
    /** Qué trae la hoja. Se repite en la portada. */
    description?: string;
    /** Aclaración al pie de los datos. */
    note?: string;
    /** Orden de las columnas. Sin esto, el de aparición en las filas. */
    headers?: string[];
    /** El autofiltro se apaga en las hojas con cortes de control. */
    autofilter?: boolean;
    /**
     * Fila de grupos arriba de los títulos de columna, para encabezados de dos
     * niveles: cada grupo se combina sobre las columnas que abarca, en orden.
     */
    headerGroups?: { label: string; span: number }[];
    /** Título a mostrar por columna, cuando la clave de la fila no sirve como título. */
    headerLabels?: Record<string, string>;
}

/** Datos del libro: van en la portada, en el encabezado de cada hoja y en las propiedades del archivo. */
export interface WorkbookMeta {
    title: string;
    /** A qué se refieren los datos: un protocolo, la cartera, un rango. */
    scope?: string;
    note?: string;
}

const NUMBER_FORMAT: Record<ExcelFormat, string | undefined> = {
    text: undefined,
    integer: '#,##0',
    // Los importes en negativo salen en rojo, como en cualquier planilla contable.
    money: '#,##0.00;[Red]-#,##0.00',
    // El número se guarda en la escala de la pantalla (42,5 = 42,5%).
    percent: '0.0"%"',
    date: 'dd/mm/yyyy'
};

export const isTypedCell = (cell: ExcelCell): cell is TypedCell =>
    typeof cell === 'object' && cell !== null && !(cell instanceof Date) && 'format' in cell;

const toDate = (value?: Date | string | number | null): Date | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

/** La misma celda, en negrita. */
export const emphasize = (cell: ExcelCell): TypedCell =>
    isTypedCell(cell)
        ? { ...cell, bold: true }
        : { value: cell === undefined || cell === null || typeof cell === 'boolean' ? String(cell ?? '') : cell, format: 'text', bold: true };

/** Importe: se guarda como número, con separador de miles y dos decimales. */
export const money = (value?: number | null): TypedCell => ({ value: value ?? undefined, format: 'money' });

/** Porcentaje en la escala de la pantalla: 42,5 se ve "42,5%". */
export const percent = (value?: number | null): TypedCell => ({ value: value ?? undefined, format: 'percent' });

/** Cantidad entera con separador de miles. */
export const integer = (value?: number | null): TypedCell => ({ value: value ?? undefined, format: 'integer' });

/** Fecha real de Excel: se ordena y se filtra como fecha, no como texto. */
export const dateCell = (value?: Date | string | number | null): TypedCell => ({
    value: toDate(value),
    format: 'date'
});

/** Valor que va a la celda; las vacías quedan en blanco y no en "undefined". */
const cellValue = (cell: ExcelCell): string | number | boolean | Date => {
    const raw = isTypedCell(cell) ? cell.value : cell;
    if (raw === undefined || raw === null) return '';
    return raw;
};

/** Cómo se va a ver la celda, para calcular el ancho de la columna. */
const cellText = (cell: ExcelCell): string => {
    const value = cellValue(cell);
    if (value === '') return '';
    if (value instanceof Date) return formatExcelDate(value);
    if (typeof value !== 'number') return String(value);
    const format = isTypedCell(cell) ? cell.format : 'text';
    if (format === 'money') return value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (format === 'percent') return `${value.toLocaleString('es-AR', { maximumFractionDigits: 1 })}%`;
    return value.toLocaleString('es-AR');
};

//#endregion Celdas con formato

//#region Nombres, fechas y encabezados

// Rango de marcas diacríticas combinables, armado por código para que no dependa de
// cómo se guarde el archivo.
const DIACRITICS = new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g');

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * Nombre de hoja válido para Excel: sin \ / ? * [ ] :, de hasta 31 caracteres y
 * sin repetir dentro del libro (Excel no distingue mayúsculas).
 */
export const sanitizeSheetName = (name: string, used: Set<string> = new Set<string>()): string => {
    const base =
        (name || 'Hoja')
            .replace(/[\\/?*[\]:]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 31) || 'Hoja';

    let candidate = base;
    let index = 2;
    while (used.has(candidate.toLowerCase())) {
        const suffix = ` (${index++})`;
        candidate = base.slice(0, 31 - suffix.length) + suffix;
    }
    used.add(candidate.toLowerCase());
    return candidate;
};

/** Nombre de archivo: el texto sin tildes ni símbolos, más la fecha del día. */
export const buildFileName = (base: string, date: Date = new Date()): string => {
    const slug =
        (base || '')
            .normalize('NFD')
            .replace(DIACRITICS, '')
            .replace(/[^A-Za-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .toLowerCase() || 'estadisticas';
    return `${slug}-${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}.xlsx`;
};

/** Fecha dd/mm/aaaa, o vacío si no hay una fecha válida. */
export const formatExcelDate = (value?: Date | string | number | null): string => {
    const date = toDate(value);
    return date ? `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}` : '';
};

/** Fecha y hora para el encabezado de las hojas. */
const formatGeneratedAt = (date: Date): string =>
    `${formatExcelDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;

/** Filas de indicadores: una por dato, con su valor y una aclaración opcional. */
export const indicatorRows = (items: [string, ExcelCell, string?][]): ExcelRow[] =>
    items.map(([label, value, detail]) => ({ Indicador: label, Valor: value ?? '', Detalle: detail ?? '' }));

/** Encabezados de todas las filas, en el orden en que aparecen. Las claves internas no son columnas. */
export const collectHeaders = (rows: ExcelRow[]): string[] => {
    const headers: string[] = [];
    const seen = new Set<string>();
    rows.forEach((row) =>
        Object.keys(row).forEach((key) => {
            if (key.startsWith('__')) return;
            if (!seen.has(key)) {
                seen.add(key);
                headers.push(key);
            }
        })
    );
    return headers;
};

/** Ancho de cada columna según su contenido, con tope para que un texto largo no la desborde. */
export const columnWidths = (rows: ExcelRow[], headers: string[]): { wch: number }[] =>
    headers.map((header) => {
        const longest = rows.reduce((max, row) => Math.max(max, cellText(row[header]).length), header.length);
        return { wch: Math.min(60, longest + 2) };
    });

//#endregion Nombres, fechas y encabezados

//#region Cortes de control

export interface ControlBreakGroup {
    /** Texto del corte: va en la primera columna del grupo. */
    label: string;
    rows: ExcelRow[];
    /** Columnas de totales, que se repiten arriba del grupo y en su subtotal. */
    totals?: ExcelRow;
}

/** Clave interna que dice qué es cada fila. No es una columna: no se escribe. */
export const ROW_KIND = '__rowKind';

/** Filas que no son de detalle: encabezado de corte, subtotal y total general. */
export type RowKind = 'group' | 'subtotal' | 'total';

const marked = (kind: RowKind, row: ExcelRow): ExcelRow => ({ ...row, [ROW_KIND]: kind });

export const rowKindOf = (row: ExcelRow): RowKind | undefined => row[ROW_KIND] as RowKind | undefined;

const sumTotals = (groups: ControlBreakGroup[]): ExcelRow => {
    const totals: Record<string, number> = {};
    groups.forEach((group) =>
        Object.entries(group.totals ?? {}).forEach(([key, cell]: [string, ExcelCell]) => {
            const value = isTypedCell(cell) ? cell.value : cell;
            if (typeof value === 'number') totals[key] = (totals[key] ?? 0) + value;
        })
    );
    return Object.fromEntries(Object.entries(totals).map(([key, value]) => [key, integer(value)]));
};

/**
 * Filas de un reporte con corte de control: cada grupo abre con su encabezado y sus
 * totales, siguen sus filas de detalle y cierra con el subtotal. Al final, el total
 * general. Es el formato de siempre para leer en papel y para controlar contra otro
 * sistema; la hoja plana equivalente queda aparte para tablas dinámicas.
 */
export const controlBreakRows = (
    groups: ControlBreakGroup[],
    groupColumn: string,
    labelColumn: string,
    grandTotalLabel = 'TOTAL GENERAL'
): ExcelRow[] => {
    const out: ExcelRow[] = [];

    groups.forEach((group, index) => {
        if (index > 0) out.push({});
        out.push(marked('group', { [groupColumn]: group.label, ...(group.totals ?? {}) }));
        group.rows.forEach((row) => out.push(row));
        if (group.totals) out.push(marked('subtotal', { [labelColumn]: `Total ${group.label}`, ...group.totals }));
    });

    if (groups.length > 1) {
        out.push({});
        out.push(marked('total', { [groupColumn]: grandTotalLabel, ...sumTotals(groups) }));
    }

    return out;
};

//#endregion Cortes de control

//#region Descarga

const HEADING_ROWS_NOTE = 'Las hojas de detalle tienen autofiltro en la fila de encabezados.';

/**
 * Paleta del archivo: el mismo azul del tablero y grises neutros, para que el Excel
 * se lea como parte del sistema y no como una exportación suelta.
 */
const COLORS = {
    band: '004578',
    softBand: 'DEECF9',
    header: '0078D4',
    white: 'FFFFFF',
    text: '323130',
    muted: '605E5C',
    grid: 'D9D9D9',
    zebra: 'F7F9FC',
    subtotal: 'EDEBE9'
};

const gridLine = { style: 'thin', color: { rgb: COLORS.grid } };
const gridBorder = { top: gridLine, bottom: gridLine, left: gridLine, right: gridLine };
const headerLine = { style: 'thin', color: { rgb: COLORS.white } };

/** Estilos de cada tipo de fila. xlsx-js-style los escribe; la versión libre de xlsx los ignoraba. */
const STYLE: Record<string, any> = {
    title: {
        font: { bold: true, sz: 14, color: { rgb: COLORS.white } },
        fill: { patternType: 'solid', fgColor: { rgb: COLORS.band } },
        alignment: { vertical: 'center' }
    },
    subtitle: {
        font: { bold: true, sz: 11, color: { rgb: COLORS.band } },
        fill: { patternType: 'solid', fgColor: { rgb: COLORS.softBand } },
        alignment: { vertical: 'center' }
    },
    caption: { font: { sz: 10, color: { rgb: COLORS.muted } }, alignment: { vertical: 'center' } },
    header: {
        font: { bold: true, sz: 11, color: { rgb: COLORS.white } },
        fill: { patternType: 'solid', fgColor: { rgb: COLORS.header } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: { top: headerLine, bottom: headerLine, left: headerLine, right: headerLine }
    },
    data: { font: { sz: 11, color: { rgb: COLORS.text } }, border: gridBorder, alignment: { vertical: 'center' } },
    zebra: {
        font: { sz: 11, color: { rgb: COLORS.text } },
        fill: { patternType: 'solid', fgColor: { rgb: COLORS.zebra } },
        border: gridBorder,
        alignment: { vertical: 'center' }
    },
    group: {
        font: { bold: true, sz: 11, color: { rgb: COLORS.band } },
        fill: { patternType: 'solid', fgColor: { rgb: COLORS.softBand } },
        border: gridBorder,
        alignment: { vertical: 'center' }
    },
    subtotal: {
        font: { bold: true, sz: 11, color: { rgb: COLORS.text } },
        fill: { patternType: 'solid', fgColor: { rgb: COLORS.subtotal } },
        border: gridBorder,
        alignment: { vertical: 'center' }
    },
    total: {
        font: { bold: true, sz: 11, color: { rgb: COLORS.white } },
        fill: { patternType: 'solid', fgColor: { rgb: COLORS.band } },
        border: gridBorder,
        alignment: { vertical: 'center' }
    },
    note: { font: { sz: 10, italic: true, color: { rgb: COLORS.muted } }, alignment: { vertical: 'center', wrapText: true } }
};

const ROW_KIND_STYLE: Record<RowKind, any> = {
    group: STYLE.group,
    subtotal: STYLE.subtotal,
    total: STYLE.total
};

/** Pinta una celda, creándola vacía si hace falta: sin celda no hay fondo que pintar. */
const styleCell = (XLSX: any, worksheet: any, row: number, column: number, style: any): void => {
    const address = XLSX.utils.encode_cell({ r: row, c: column });
    if (!worksheet[address]) worksheet[address] = { t: 's', v: '' };
    worksheet[address].s = style;
};

const styleRow = (XLSX: any, worksheet: any, row: number, columns: number, style: any): void => {
    for (let column = 0; column < columns; column += 1) styleCell(XLSX, worksheet, row, column, style);
};

/** Una fila sin ningún valor visible: separa cortes y no se pinta. */
const isBlankRow = (row: ExcelRow, headers: string[]): boolean =>
    headers.every((header) => cellValue(row[header]) === '');

/** Arma una hoja: bloque de encabezado, tabla con autofiltro y aclaración al pie. */
const buildWorksheet = (
    XLSX: any,
    sheet: ExcelSheet,
    meta: WorkbookMeta | undefined,
    generatedAt: Date
) => {
    const rows = sheet.rows.length > 0 ? sheet.rows : [{ Aviso: 'Sin datos para esta sección' }];
    const headers = sheet.headers ?? collectHeaders(rows);
    const sheetTitle = sheet.title ?? sheet.name;

    const heading = [
        meta?.title ?? sheetTitle,
        meta ? `${sheetTitle}${meta.scope ? ` · ${meta.scope}` : ''}` : sheet.description,
        meta ? sheet.description : undefined,
        `Generado el ${formatGeneratedAt(generatedAt)} · ${sheet.rows.length} ${sheet.rows.length === 1 ? 'fila' : 'filas'}`
    ].filter((line): line is string => !!line);

    // Con grupos, los títulos de columna bajan una fila: arriba va el grupo.
    const groups = sheet.headerGroups ?? [];
    const groupRow = groups.length > 0 ? heading.length + 1 : undefined;
    const headerRow = heading.length + 1 + (groupRow !== undefined ? 1 : 0);

    const groupCells: string[] = [];
    groups.forEach((group) => {
        groupCells.push(group.label);
        for (let index = 1; index < group.span; index += 1) groupCells.push('');
    });

    const data = rows.map((row) => headers.map((header) => cellValue(row[header])));
    const matrix: (string | number | boolean | Date)[][] = [
        ...heading.map((line) => [line]),
        [],
        ...(groupRow !== undefined ? [groupCells] : []),
        headers.map((header) => sheet.headerLabels?.[header] ?? header),
        ...data
    ];

    const lastRow = headerRow + rows.length;
    if (sheet.note) matrix.push([], [sheet.note]);

    const worksheet = XLSX.utils.aoa_to_sheet(matrix, { cellDates: true });
    worksheet['!cols'] = columnWidths(rows, headers);

    // Formato de número, moneda, porcentaje y fecha, celda por celda.
    rows.forEach((row, rowIndex) =>
        headers.forEach((header, columnIndex) => {
            const cell = row[header];
            if (!isTypedCell(cell)) return;
            const format = NUMBER_FORMAT[cell.format];
            const address = XLSX.utils.encode_cell({ r: headerRow + 1 + rowIndex, c: columnIndex });
            const target = worksheet[address];
            if (format && target && target.v !== '') target.z = format;
        })
    );

    // El título y las aclaraciones ocupan el ancho de la tabla.
    if (headers.length > 1) {
        const merges = heading.map((_, index) => ({ s: { r: index, c: 0 }, e: { r: index, c: headers.length - 1 } }));
        if (sheet.note) merges.push({ s: { r: lastRow + 2, c: 0 }, e: { r: lastRow + 2, c: headers.length - 1 } });

        if (groupRow !== undefined) {
            let start = 0;
            groups.forEach((group) => {
                if (group.span > 1) merges.push({ s: { r: groupRow, c: start }, e: { r: groupRow, c: start + group.span - 1 } });
                start += group.span;
            });
        }

        worksheet['!merges'] = merges;
    }

    if (sheet.autofilter !== false && sheet.rows.length > 0) {
        const lastColumn = XLSX.utils.encode_col(headers.length - 1);
        worksheet['!autofilter'] = { ref: `A${headerRow + 1}:${lastColumn}${lastRow + 1}` };
    }

    // ── Colores ──
    // Encabezado del reporte, fila de títulos de columna y filas de datos. Los cortes
    // de control se distinguen del detalle: el resto alterna un gris muy suave para
    // no perder el renglón en las tablas largas.
    heading.forEach((_, index) => {
        let style = STYLE.caption;
        if (index === 0) style = STYLE.title;
        else if (index === 1 && meta) style = STYLE.subtitle;
        styleRow(XLSX, worksheet, index, headers.length, style);
    });

    if (groupRow !== undefined) styleRow(XLSX, worksheet, groupRow, headers.length, STYLE.header);
    styleRow(XLSX, worksheet, headerRow, headers.length, STYLE.header);

    rows.forEach((row, index) => {
        if (isBlankRow(row, headers)) return;
        const kind = rowKindOf(row);
        const style = kind ? ROW_KIND_STYLE[kind] : index % 2 === 1 ? STYLE.zebra : STYLE.data;
        styleRow(XLSX, worksheet, headerRow + 1 + index, headers.length, style);
    });

    // Negrita por celda, encima del estilo de la fila. El estilo se copia: styleRow
    // comparte el mismo objeto entre todas las celdas del renglón.
    rows.forEach((row, rowIndex) =>
        headers.forEach((header, columnIndex) => {
            const cell = row[header];
            if (!isTypedCell(cell) || !cell.bold) return;
            const address = XLSX.utils.encode_cell({ r: headerRow + 1 + rowIndex, c: columnIndex });
            const target = worksheet[address];
            if (!target) return;
            const style = target.s ?? {};
            target.s = { ...style, font: { ...(style.font ?? {}), bold: true } };
        })
    );

    if (sheet.note) styleRow(XLSX, worksheet, lastRow + 2, headers.length, STYLE.note);

    const heights: { hpt: number }[] = heading.map((_, index) => ({ hpt: index === 0 ? 26 : 18 }));
    heights.push({ hpt: 8 });
    if (groupRow !== undefined) heights.push({ hpt: 22 });
    heights.push({ hpt: 22 });
    worksheet['!rows'] = heights;

    return worksheet;
};

/** Portada: qué trae cada hoja y cuántas filas tiene. */
const coverSheet = (sheets: ExcelSheet[], names: string[], meta: WorkbookMeta): ExcelSheet => ({
    name: 'Portada',
    title: 'Contenido del archivo',
    description: meta.note,
    headers: ['Hoja', 'Contenido', 'Filas'],
    autofilter: false,
    note: HEADING_ROWS_NOTE,
    rows: sheets.map((sheet, index) => ({
        Hoja: names[index],
        Contenido: sheet.description ?? sheet.title ?? sheet.name,
        Filas: integer(sheet.rows.length)
    }))
});

/** Arma el libro y lo descarga. Una hoja vacía lleva un aviso en vez de quedar en blanco. */
export const downloadWorkbook = async (
    fileName: string,
    sheets: ExcelSheet[],
    meta?: WorkbookMeta
): Promise<void> => {
    // xlsx-js-style es el mismo SheetJS con soporte de estilos de celda: misma API.
    const XLSX: any = await import('xlsx-js-style');
    const workbook = XLSX.utils.book_new();
    const used = new Set<string>();
    const generatedAt = new Date();

    const filled = sheets.length > 0 ? sheets : [{ name: 'Datos', rows: [] }];
    const names = filled.map((sheet) => sanitizeSheetName(sheet.name, used));

    if (meta && filled.length > 1) {
        const cover = coverSheet(filled, names, meta);
        XLSX.utils.book_append_sheet(
            workbook,
            buildWorksheet(XLSX, cover, meta, generatedAt),
            sanitizeSheetName(cover.name, used)
        );
    }

    filled.forEach((sheet, index) =>
        XLSX.utils.book_append_sheet(workbook, buildWorksheet(XLSX, sheet, meta, generatedAt), names[index])
    );

    workbook.Props = {
        Title: meta?.title ?? fileName,
        Subject: meta?.scope,
        Author: 'PreBiller',
        CreatedDate: generatedAt
    };

    XLSX.writeFile(workbook, fileName);
};

//#endregion Descarga

//#region Estado de los pacientes

const patientRow = (item: ProtocolPatientStatusDetail): ExcelRow => ({
    Paciente: item.patientFullName ?? '',
    'N° en protocolo': item.numberInProtocol ?? '',
    Estado: PatientFunctions.statusLabel(item.status, item.statusDescription),
    Etapa: PatientFunctions.STAGE_LABEL[PatientFunctions.statusStage(item.status)],
    'Fecha de randomización': dateCell(item.randomizationDate)
});

const PATIENT_GROUP_HEADERS = [
    'Protocolo',
    'Paciente',
    'N° en protocolo',
    'Estado',
    'Etapa',
    'Fecha de randomización',
    'Pacientes',
    'En selección',
    'Fallas de selección',
    'Randomizados'
];

const protocolLabel = (item: ProtocolPatientStatusDetail): string =>
    item.protocolNumber || `#${item.protocolId}`;

/**
 * Pacientes agrupados por protocolo, con corte de control: encabezado del protocolo
 * con sus cantidades por etapa, el detalle de sus pacientes, el subtotal y, al
 * final, el total general.
 */
export const patientsByProtocolSheet = (
    details: ProtocolPatientStatusDetail[],
    name = 'Pacientes por protocolo',
    description = 'Pacientes agrupados por protocolo, con subtotales por etapa en cada corte.'
): ExcelSheet => {
    const byProtocol = new Map<number, ProtocolPatientStatusDetail[]>();
    (details ?? []).forEach((item) => {
        const current = byProtocol.get(item.protocolId);
        if (current) current.push(item);
        else byProtocol.set(item.protocolId, [item]);
    });

    const groups: ControlBreakGroup[] = Array.from(byProtocol.values())
        .map((items) => {
            const stages = items.map((item) => PatientFunctions.statusStage(item.status));
            return {
                label: protocolLabel(items[0]),
                rows: PatientFunctions.sortDetailsByName(items).map(patientRow),
                totals: {
                    Pacientes: integer(items.length),
                    'En selección': integer(stages.filter((stage) => stage === 'selection').length),
                    'Fallas de selección': integer(stages.filter((stage) => stage === 'screenFailure').length),
                    Randomizados: integer(stages.filter((stage) => stage === 'randomized').length)
                }
            };
        })
        .sort((a, b) => a.label.localeCompare(b.label, 'es', { numeric: true, sensitivity: 'base' }));

    return {
        name,
        description,
        headers: PATIENT_GROUP_HEADERS,
        autofilter: false,
        note: 'Cada bloque es un protocolo: arriba sus totales, abajo el subtotal. La hoja "Pacientes" trae lo mismo en una tabla plana.',
        rows: controlBreakRows(groups, 'Protocolo', 'Paciente')
    };
};

/**
 * Hojas de "Estado de los pacientes": etapas, estados, nomenclador y la lista
 * completa de pacientes. En la cartera se suma el mismo detalle con corte de
 * control por protocolo. La historia clínica queda afuera, como en la pantalla.
 */
export const patientStatusSheets = (
    statistics: ProtocolPatientStatusStatistics | undefined,
    details: ProtocolPatientStatusDetail[],
    allProtocols: boolean
): ExcelSheet[] => {
    const rows = PatientFunctions.buildStatusRows(statistics);
    const total = statistics?.total ?? rows.reduce((acc, row) => acc + row.count, 0);
    const groups = PatientFunctions.buildStageGroups(rows, total);

    const sheets: ExcelSheet[] = [
        {
            name: 'Etapas',
            description: 'Pacientes por etapa del estudio.',
            rows: [
                { Etapa: 'Total', Pacientes: total, '% del total': percent(total > 0 ? 100 : 0) },
                ...groups.map((group) => ({
                    Etapa: group.label,
                    Pacientes: group.count,
                    '% del total': percent(group.percentage)
                }))
            ]
        },
        {
            name: 'Estados',
            description: 'Pacientes por estado, con la etapa a la que pertenece cada uno.',
            rows: rows.map((row) => ({
                Código: row.status,
                Estado: row.label,
                Etapa: PatientFunctions.STAGE_LABEL[row.stage],
                Pacientes: row.count,
                '% del total': percent(row.percentage)
            }))
        },
        {
            name: 'Nomenclador',
            description: 'Todos los estados posibles del glosario, estén o no en uso.',
            rows: PatientFunctions.buildStatusCatalog(statistics).map((entry) => ({
                Código: entry.status,
                Estado: entry.name,
                'Nombre informado': entry.description ?? '',
                Etapa: PatientFunctions.STAGE_LABEL[entry.stage],
                Pacientes: entry.countInProtocol
            }))
        },
        {
            name: 'Pacientes',
            description: 'Detalle de pacientes, ordenado por nombre.',
            rows: PatientFunctions.sortDetailsByName(details).map((item) => ({
                ...(allProtocols ? { Protocolo: protocolLabel(item) } : {}),
                ...patientRow(item)
            }))
        }
    ];

    if (allProtocols) sheets.push(patientsByProtocolSheet(details));

    return sheets;
};

//#endregion Estado de los pacientes

//#region Ejecución de visitas

const CELL_LABEL: Record<VisitCellState, string> = {
    done: 'Realizada',
    started: 'Iniciada',
    skipped: 'Salteada',
    failed: 'Falla de selección'
};

/**
 * Hojas de "Ejecución de visitas": resumen, línea de tiempo y la matriz completa
 * paciente × visita. Dos visitas con el mismo nombre llevan su identificador para
 * no pisarse en la misma columna.
 */
export const visitExecutionSheets = (model: VisitExecutionModel): ExcelSheet[] => {
    const { progress, columns, rows, timeline } = model;

    const nameCount = new Map<string, number>();
    columns.forEach((column) => nameCount.set(column.name, (nameCount.get(column.name) ?? 0) + 1));
    const columnTitle = (column: VisitExecutionModel['columns'][number]): string => {
        const title = (nameCount.get(column.name) ?? 0) > 1 ? `${column.name} (${column.key})` : column.name;
        return column.isOptional ? `${title} (opcional)` : title;
    };

    return [
        {
            name: 'Resumen',
            description: 'Avance del plan de visitas.',
            rows: indicatorRows([
                ['Visitas esperadas realizadas', progress.doneExpected],
                ['Visitas esperadas', progress.expected, 'Visitas obligatorias × pacientes randomizados'],
                ['% de avance', percent(progress.percentage)],
                ['Visitas por paciente', columns.length],
                ['Visitas obligatorias', progress.requiredVisits],
                ['Pacientes randomizados', progress.randomizedPatients],
                ['Otras visitas realizadas', progress.doneTotal - progress.doneExpected, 'Opcionales o de pacientes no randomizados'],
                ['Visitas fuera del plan', model.extraVisits],
                ['Visitas realizadas sin fecha', timeline.undated]
            ])
        },
        {
            name: 'Línea de tiempo',
            description: 'Visitas realizadas por mes, con el acumulado.',
            rows: timeline.points.map((point) => ({
                Mes: formatMonth(point.month),
                Período: point.month,
                'Visitas del mes': point.count,
                Acumulado: point.cumulative
            }))
        },
        {
            name: 'Detalle por paciente',
            description: 'Matriz paciente × visita, con el estado y la fecha de cada visita.',
            rows: rows.map((row) => {
                const out: ExcelRow = {
                    'N° en protocolo': row.numberInProtocol,
                    'Estado del paciente': PatientFunctions.statusLabel(row.status, row.statusDescription),
                    Etapa: PatientFunctions.STAGE_LABEL[row.stage]
                };
                columns.forEach((column) => {
                    const cell = row.cells.get(column.key);
                    const date = cell?.time !== undefined ? formatExcelDate(cell.time) : '';
                    out[columnTitle(column)] = cell ? `${CELL_LABEL[cell.state]}${date ? ' ' + date : ''}` : 'Pendiente';
                });
                out['Obligatorias realizadas'] = row.doneRequired;
                return out;
            })
        }
    ];
};

//#endregion Ejecución de visitas
